/**
 * The Today events' wire names and the two property builders that carry no view-model with them
 * (IPD-2974).
 *
 * Split from {@link use-today-events} for one reason, and it is a change-control one: the screen
 * that reports a finished task is `intervention-session-completed-page.vue`, which is a medical
 * device entry point, so whatever it imports enters the manifest's scope. Everything here is
 * literals and needs no import at all, which is what keeps `types/component/today-page.ts` — the
 * tile and face enums, of no use to that screen — out of it.
 *
 * `use-today-events.ts` re-exports all three, so the Today page's own imports are unchanged.
 */

/**
 * Renamed from the three the ticket proposed, which collided with each other and with the
 * backend's: `today_content_seen` became `shown` because *seen*, *viewed* and *opened* were three
 * synonyms for three different things and `block_content_viewed` already means "opened";
 * `today_video_viewed` became `today_intro_watched` because the piece is the welcome one rather
 * than any video (nothing guarantees it even carries one) and *watched* is the verb the backend
 * uses for the same act; and `today_content_opened` folded into {@link TodayEvent.tileClicked},
 * which fires on the same click with all but two of the same properties.
 */
export enum TodayEvent {
  pageViewed = 'today_page_viewed',
  tileViewed = 'today_tile_viewed',
  tileClicked = 'today_tile_clicked',
  contentShown = 'today_content_shown',
  introWatched = 'today_intro_watched',
  /**
   * Not one of the seven the ticket lists, and the gap it fills is real: the journal tile is
   * written in place, so an entry saved from this page is neither a click through to somewhere
   * else nor a task completed, and none of the others can see it. It is a conversion the page
   * earns on its own.
   */
  journalSaved = 'today_journal_saved',
  /**
   * A task opened from this page and then finished. Measurement, not the fact: the fact already
   * exists as the backend's own `session_completed` and the rest, which carry the task but not
   * where the user came from. Reported by the browser, from the screen that says the task is done.
   */
  taskCompleted = 'today_task_completed',
}

/* eslint-disable @typescript-eslint/naming-convention -- these are property names as they are
   written on the wire, in the same snake_case the backend's own events use. */

/**
 * The two properties every Today event carries (IPD-2974 8.3).
 *
 * The locale arrives as an argument rather than being read here: this module is pure, and the
 * language lives behind a composable. `platform` is spelled out rather than sniffed from the user
 * agent, because the question it answers is which client sent the event, not which device ran it.
 * The iOS app is a separate repository and would report its own.
 */
export function commonProperties(locale: string): Record<string, unknown> {
  return {
    platform: 'web',
    locale,
  };
}

/**
 * A task that was opened from the Today page and then finished.
 *
 * `source` is the value the ticket asks for, and it is constant: the event only exists for tasks
 * that came from this page, so it says so rather than leaving the reader to infer it from the
 * event name.
 */
export function taskProperties(taskType: string, taskId: string): Record<string, unknown> {
  return {
    task_type: taskType,
    task_id: taskId,
    source: 'today_page',
  };
}

/* eslint-enable @typescript-eslint/naming-convention */
